
import { useState, useCallback, useEffect } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';

const SESSION_ICON = { Race:'🏁', Qualifying:'⏱', Practice:'🔧', Sprint:'⚡', 'Sprint Qualifying':'⚡' };

function LeagueTable({ table, status }) {
  if (!table || table.length === 0) return (
    <p style={{color:'#4a6a9e',fontSize:'0.74rem',textAlign:'center'}}>
      Waiting for predictions...
    </p>
  );
  const hdr = status === 'finished' ? '🏆 Final Standings'
            : status === 'live'     ? '📊 Live Standings'
            :                         '📋 Predictions Submitted';
  return (
    <div className="pq-scoreboard" style={{width:'100%'}}>
      <div className="pq-score-hdr">{hdr}</div>
      {table.map((row, i) => (
        <div key={row.name} className="pq-score-row"
          style={{flexDirection:'column', alignItems:'flex-start', gap:3}}>
          <div style={{display:'flex', width:'100%', alignItems:'center', gap:8}}>
            <span className="pq-score-pos">{i===0?'🥇':i===1?'🥈':i===2?'🥉':('#'+(i+1))}</span>
            <span className="pq-score-name" style={{flex:1}}>{row.name}</span>
            <span className="pq-score-pts">{row.pts} pts</span>
          </div>
          {row.prediction && (
            <div style={{paddingLeft:28, fontSize:'0.63rem', color:'#4a6a9e', lineHeight:1.6}}>
              {row.prediction.winner && <span>🏆 {row.prediction.winner}  </span>}
              {(row.prediction.podium||[]).length > 1 && (
                <span>🥈 {row.prediction.podium[1]} 🥉 {row.prediction.podium[2] || '?'}  </span>
              )}
              {row.prediction.safetyCar !== undefined && (
                <span>{row.prediction.safetyCar ? '🚗 SC' : '🚫 No SC'}</span>
              )}
            </div>
          )}
          {row.breakdown?.length > 0 && (
            <div style={{paddingLeft:28, display:'flex', flexWrap:'wrap', gap:4}}>
              {row.breakdown.map((b,j) => (
                <span key={j} style={{fontSize:'0.57rem', color:'#4ade80',
                  background:'rgba(34,197,94,0.1)', borderRadius:4, padding:'1px 6px'}}>
                  +{b.pts} {b.desc}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function F1Predictor({ onBack }) {
  const [phase, setPhase]       = useState('pick');
  const [sessions, setSessions] = useState([]);
  const [chosen, setChosen]     = useState(null);
  const [code, setCode]         = useState('');
  const [serverState, setServerState] = useState(null);
  const [joiners, setJoiners]   = useState([]);

  const onMsg = useCallback((msg) => {
    if (msg.type === 'F1_SESSIONS')          { setSessions(msg.sessions); return; }
    if (msg.type === 'F1_PREDICTOR_CREATED') { setCode(msg.code); setPhase('lobby'); return; }
    if (msg.type === 'F1_PLAYER_JOINED')     { setJoiners(j => [...j, msg.name]); return; }
    if (msg.type === 'F1_PREDICTOR_STATE') {
      setServerState(msg);
      if (msg.status === 'finished') setPhase('finished');
      else if (msg.status === 'live') setPhase('live');
      else setPhase('lobby');
    }
  }, []);

  const { send } = useWebSocket(onMsg);
  useEffect(() => { send('F1_GET_SESSIONS'); }, [send]);

  useEffect(() => {
    const t = setTimeout(() => {},0); // kept for structure
    return () => clearTimeout(t);
  }, [send]);

  const createSession = () => {
    if (!chosen) return;
    send('F1_PREDICTOR_CREATE', { sessionKey: chosen.sessionKey });
  };

  const table      = serverState?.table || [];
  const lb         = serverState?.snapshot?.leaderboard || [];
  const result     = serverState?.result || null;
  const predCount  = Object.keys(serverState?.predictions || {}).length;
  const f1Session  = serverState?.session || chosen;

  // Group sessions by meeting for track-grouped display
  const meetingGroups = (() => {
    const map = {};
    sessions.forEach(s => {
      const key = s.meetingKey || s.meetingName;
      if (!map[key]) map[key] = [];
      map[key].push(s);
    });
    return Object.entries(map)
      .map(([key, sess]) => ({
        name: sess[0].meetingName,
        country: sess[0].countryName,
        sessions: [...sess].sort((a,b) => new Date(a.dateStart) - new Date(b.dateStart)),
        firstStart: Math.min(...sess.map(s => new Date(s.dateStart).getTime())),
      }))
      .sort((a,b) => a.firstStart - b.firstStart);
  })();

  // ── SESSION PICKER ────────────────────────────────────────────────────
  if (phase === 'pick') return (
    <div className="layer" style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
      <div className="g-hdr f1-hdr">
        <button className="g-back" onClick={onBack}>← Back</button>
        <div className="g-title" style={{color:'#e8002d'}}>F1 PREDICTOR HOST</div>
      </div>
      <div className="f1-live-wrap">
        <div style={{fontSize:'0.55rem',letterSpacing:'0.18em',color:'#4a6a9e',textTransform:'uppercase'}}>
          Pick a 2026 session to predict
        </div>

        {sessions.length === 0 && (
          <p style={{color:'#4a6a9e',textAlign:'center',fontSize:'0.8rem'}}>Loading 2026 sessions...</p>
        )}

        {meetingGroups.map(meeting => {
          const now       = Date.now();
          const dur       = { Race:2*3600*1000, Qualifying:75*60*1000, Practice:90*60*1000, Sprint:60*60*1000 };
          const lastEnd   = Math.max(...meeting.sessions.map(s => {
            const st = new Date(s.dateStart).getTime();
            return s.dateEnd ? new Date(s.dateEnd).getTime() : st + (dur[s.sessionType]||90*60*1000);
          }));
          const isLive    = now >= meeting.firstStart && now <= lastEnd;
          const isPast    = now > lastEnd;
          const raceS     = meeting.sessions.find(s=>s.sessionType==='Race') || meeting.sessions[meeting.sessions.length-1];
          const borderCol = isLive ? '#e8002d' : !isPast ? '#0369a1' : '#1e3a6e';
          const headCol   = isLive ? '#e8002d' : !isPast ? '#38bdf8' : '#8ba3cc';
          const badge     = isLive ? '● LIVE' : !isPast ? 'UPCOMING' : 'PAST';
          const badgeCol  = isLive ? '#e8002d' : !isPast ? '#38bdf8' : '#3a5a8e';

          return (
            <div key={meeting.name} style={{borderRadius:12,overflow:'hidden',
              border:`1px solid ${borderCol}`,
              opacity: isPast ? 0.55 : 1,
              background:'#0a1628'}}>

              {/* Track header */}
              <div style={{padding:'11px 14px',background: isLive?'rgba(232,0,45,0.08)':!isPast?'rgba(56,189,248,0.05)':'#0f1e38',
                borderBottom:'1px solid #1e3a6e',display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:8}}>
                <div>
                  <div style={{fontSize:'1rem',fontWeight:700,color:headCol,lineHeight:1.2}}>
                    {meeting.country}
                  </div>
                  <div style={{fontSize:'0.74rem',color:'#8ba3cc',marginTop:1}}>{meeting.name}</div>
                  <div style={{fontSize:'0.6rem',color:'#4a6a9e',marginTop:3}}>
                    {new Date(raceS.dateStart).toLocaleDateString([],{weekday:'short',day:'numeric',month:'long',year:'numeric'})}
                  </div>
                </div>
                <span style={{fontSize:'0.5rem',fontWeight:700,letterSpacing:'0.12em',color:badgeCol,
                  textTransform:'uppercase',padding:'2px 7px',borderRadius:4,
                  border:`1px solid ${badgeCol}`,background:`${badgeCol}18`,
                  whiteSpace:'nowrap',marginTop:2,flexShrink:0}}>
                  {badge}
                </span>
              </div>

              {/* Session buttons */}
              {meeting.sessions.map((s,si) => {
                const sStart  = new Date(s.dateStart).getTime();
                const sEnd    = s.dateEnd ? new Date(s.dateEnd).getTime() : sStart+(dur[s.sessionType]||90*60*1000);
                const sLive   = now>=sStart && now<=sEnd;
                const sPast   = now>sEnd;
                const isChosen = chosen?.sessionKey === s.sessionKey;
                return (
                  <button key={s.sessionKey}
                    onClick={() => setChosen(s)}
                    style={{width:'100%',display:'flex',alignItems:'center',gap:10,padding:'9px 14px',
                      background: isChosen ? 'rgba(232,0,45,0.12)' : 'transparent',
                      border:'none',borderBottom: si<meeting.sessions.length-1?'1px solid #0f1e38':'none',
                      cursor: isPast&&!sLive ? 'default':'pointer',
                      transition:'background 0.14s',textAlign:'left',fontFamily:'Inter,sans-serif'}}>
                    <span style={{fontSize:'1rem',width:22,textAlign:'center',flexShrink:0}}>
                      {SESSION_ICON[s.sessionType]||'🏎️'}
                    </span>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:'0.82rem',fontWeight:600,
                        color: isChosen?'#e8002d':sLive?'#e8002d':sPast?'#4a6a9e':'#e8edf8'}}>
                        {s.sessionName}
                      </div>
                      <div style={{fontSize:'0.6rem',color:'#4a6a9e'}}>
                        {new Date(s.dateStart).toLocaleString([],{weekday:'short',day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}
                      </div>
                    </div>
                    {sLive  && <span style={{fontSize:'0.52rem',color:'#e8002d',fontWeight:700,animation:'pulse 1.5s ease infinite',flexShrink:0}}>● LIVE</span>}
                    {!sPast && !sLive && <span style={{fontSize:'0.52rem',color:'#38bdf8',flexShrink:0}}>UPCOMING</span>}
                    {sPast  && !sLive && <span style={{fontSize:'0.52rem',color:'#3a5a8e',flexShrink:0}}>FIN</span>}
                    {isChosen && <span style={{fontSize:'0.8rem',color:'#e8002d',flexShrink:0}}>✓</span>}
                  </button>
                );
              })}
            </div>
          );
        })}

        {chosen && (
          <div style={{position:'sticky',bottom:12,background:'#080f1e',borderTop:'1px solid #1e3a6e',paddingTop:12}}>
            <div style={{textAlign:'center',marginBottom:8}}>
              <div style={{fontSize:'0.72rem',color:'#8ba3cc'}}>{chosen.countryName} · {chosen.meetingName}</div>
              <div style={{fontSize:'0.88rem',fontWeight:700,color:'#e8002d',marginTop:2}}>
                {SESSION_ICON[chosen.sessionType]||'🏎️'} {chosen.sessionName} · {new Date(chosen.dateStart).toLocaleDateString([],{day:'numeric',month:'short'})}
              </div>
            </div>
            <button onClick={createSession} style={{width:'100%',padding:'11px',border:'none',
              borderRadius:10,background:'linear-gradient(135deg,#e8002d,#8b0015)',color:'#fff',
              fontFamily:"'Bebas Neue',sans-serif",fontSize:'1.2rem',letterSpacing:'0.1em',cursor:'pointer'}}>
              CREATE PREDICTOR SESSION
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // ── LOBBY / LIVE / FINISHED ───────────────────────────────────────────
  return (
    <div className="layer" style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
      <div className="g-hdr f1-hdr">
        <button className="g-back" onClick={onBack}>← Menu</button>
        <div className="g-title" style={{color:'#e8002d'}}>
          {phase === 'finished' ? '🏁 FINISHED' : phase === 'live' ? '● LIVE' : 'HOST LOBBY'}
        </div>
        <div className="g-pills">
          <div className="g-pill f1-pill"><span className="g-plbl">Code</span><span className="g-pval" style={{color:'#e8002d'}}>{code}</span></div>
          <div className="g-pill f1-pill"><span className="g-plbl">Players</span><span className="g-pval">{predCount}</span></div>
        </div>
      </div>

      <div className="f1-live-wrap">
        {/* Session header */}
        {f1Session && (
          <div style={{background:'#0f1e38',border:'1px solid #3a0a10',borderRadius:12,padding:'14px 16px',textAlign:'center'}}>
            <div style={{fontSize:'0.58rem',color:'#4a6a9e',textTransform:'uppercase',letterSpacing:'0.15em'}}>
              {f1Session.meetingName} · {f1Session.countryName || '2026'}
            </div>
            <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:'1.8rem',color:'#e8002d',marginTop:4}}>
              {SESSION_ICON[f1Session.sessionType]||'🏎️'} {f1Session.sessionName}
            </div>
            <div style={{fontSize:'0.62rem',color:'#4a6a9e',marginTop:2}}>
              {new Date(f1Session.dateStart).toLocaleString([],{weekday:'short',day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}
            </div>
          </div>
        )}

        {/* Session code */}
        <div style={{background:'#0a1628',border:'2px solid #3a0a10',borderRadius:12,padding:'14px',textAlign:'center'}}>
          <div style={{fontSize:'0.55rem',letterSpacing:'0.2em',color:'#4a6a9e',textTransform:'uppercase'}}>
            Session Code — share with players
          </div>
          <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:'3.2rem',color:'#e8002d',
            letterSpacing:'0.15em',textShadow:'0 0 40px rgba(232,0,45,0.4)'}}>
            {code}
          </div>
          <div style={{fontSize:'0.62rem',color:'#4a6a9e'}}>
            Players: Menu → F1 → Join Predictor · Session persists 24h
          </div>
        </div>

        {/* Live mini-leaderboard */}
        {lb.length > 0 && phase === 'live' && (
          <div style={{background:'#0f1e38',border:'1px solid #3a0a10',borderRadius:10,padding:'10px 14px'}}>
            <div style={{fontSize:'0.52rem',color:'#e8002d',textTransform:'uppercase',letterSpacing:'0.15em',marginBottom:6}}>
              ● Live Race Order
            </div>
            {lb.slice(0,5).map((d,i) => (
              <div key={d.driverNumber||i} style={{display:'flex',alignItems:'center',gap:8,
                padding:'4px 0',borderBottom:'1px solid #0a1628'}}>
                <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:'1.1rem',width:20,
                  color:i===0?'#ffd700':i===1?'#c0c0c0':i===2?'#cd7f32':'#3a5a8e'}}>
                  {d.position||i+1}
                </span>
                <div style={{width:3,height:22,borderRadius:2,background:d.teamColour||'#3a5a8e',flexShrink:0}}/>
                <span style={{flex:1,fontSize:'0.8rem',fontWeight:700,color:'#e8edf8'}}>{d.acronym}</span>
                <span style={{fontSize:'0.65rem',color:'#4a6a9e'}}>{d.teamName?.split(' ').pop()}</span>
                {d.inPit && <span className="f1-pit-badge">PIT</span>}
              </div>
            ))}
          </div>
        )}

        {/* Result panel when available */}
        {result && (
          <div style={{background:'#0f1e38',border:'1px solid #3a0a10',borderRadius:10,padding:'12px 14px'}}>
            <div style={{fontSize:'0.52rem',color:'#e8002d',textTransform:'uppercase',letterSpacing:'0.15em',marginBottom:6}}>
              Current Result
            </div>
            {result.winner && <div style={{fontSize:'0.9rem',color:'#ffd700',fontWeight:700}}>🏆 {result.winner}</div>}
            {result.podium?.length > 0 && (
              <div style={{fontSize:'0.78rem',color:'#c0c0c0',marginTop:3}}>
                🥇 {result.podium[0]} · 🥈 {result.podium[1]} · 🥉 {result.podium[2]}
              </div>
            )}
            {result.safetyCar !== undefined && (
              <div style={{fontSize:'0.72rem',color:'#f5c518',marginTop:3}}>
                {result.safetyCar ? '🚗 Safety Car deployed' : '🚫 No Safety Car'}
              </div>
            )}
          </div>
        )}

        {/* Recent joiners */}
        {joiners.length > 0 && phase === 'lobby' && (
          <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
            <span style={{fontSize:'0.62rem',color:'#4a6a9e'}}>Joined:</span>
            {joiners.slice(-8).map((n,i) => (
              <span key={i} style={{fontSize:'0.64rem',color:'#e8002d',
                background:'rgba(232,0,45,0.1)',borderRadius:4,padding:'1px 7px'}}>{n}</span>
            ))}
          </div>
        )}

        {predCount === 0 && phase === 'lobby' && (
          <p style={{color:'#4a6a9e',fontSize:'0.74rem',textAlign:'center'}}>
            Waiting for players to join and submit predictions...
          </p>
        )}

        <LeagueTable table={table} status={serverState?.status}/>

        {phase === 'finished' && (
          <p style={{fontSize:'0.7rem',color:'#4a6a9e',textAlign:'center'}}>
            Session open 24h · Code <strong style={{color:'#e8002d'}}>{code}</strong>
          </p>
        )}

        <button onClick={() => send('F1_GET_SNAPSHOT')}
          style={{background:'transparent',border:'1px solid #3a0a10',borderRadius:8,
            color:'#e8002d',padding:'7px 14px',cursor:'pointer',fontSize:'0.74rem',
            fontFamily:'Inter,sans-serif'}}>
          ↻ Refresh Data
        </button>
      </div>
    </div>
  );
}
